/**
 * Thin wrapper around Plausible analytics.
 *
 * Plausible is **not** loaded at boot — Task #202 (GDPR cookie banner)
 * gates non-essential scripts behind explicit visitor consent. The
 * cookie-consent provider calls `loadPlausible()` once the visitor
 * accepts the Analytics category, and `unloadPlausible()` if consent
 * is withdrawn during the same session.
 *
 * `trackEvent` is safe to call regardless of consent state — it
 * silently no-ops when `window.plausible` isn't installed, so callers
 * (CTA clicks, scroll-depth, etc.) don't need to know whether the
 * script has mounted yet.
 */
import { useEffect, useRef } from "react";

type PlausibleProps = Record<string, string | number | boolean>;

declare global {
  interface Window {
    plausible?: ((eventName: string, options?: { props?: PlausibleProps }) => void) & {
      q?: unknown[];
      o?: unknown;
    };
  }
}

const PLAUSIBLE_SRC = "https://plausible.io/js/pa-KP-ikIxzmw8b8US41g-pv.js";
const PLAUSIBLE_SCRIPT_ID = "plausible-analytics-script";
let plausibleLoaded = false;

/**
 * Inject the Plausible snippet into the document and initialise the
 * `window.plausible` queue. Idempotent — safe to call repeatedly.
 */
export function loadPlausible(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (plausibleLoaded) return;
  plausibleLoaded = true;

  // Install the queue stub *before* the async script lands so any
  // events fired in the meantime are captured.
  if (typeof window.plausible !== "function") {
    const stub = function plausibleStub(...args: unknown[]) {
      stub.q = stub.q ?? [];
      stub.q.push(args);
    } as Window["plausible"] & { q: unknown[]; o?: unknown };
    stub.q = [];
    window.plausible = stub;
  }

  if (!document.getElementById(PLAUSIBLE_SCRIPT_ID)) {
    const s = document.createElement("script");
    s.id = PLAUSIBLE_SCRIPT_ID;
    s.async = true;
    s.defer = true;
    s.src = PLAUSIBLE_SRC;
    document.head.appendChild(s);
  }

  // The official snippet calls `plausible.init()` after registering
  // the queue. Mirror that so pageviews are auto-tracked once the
  // script lands.
  if (window.plausible && typeof (window.plausible as unknown as { init?: unknown }).init !== "function") {
    (window.plausible as unknown as { init: (i?: unknown) => void }).init = function init(i?: unknown) {
      (window.plausible as unknown as { o?: unknown }).o = i ?? {};
    };
  }
  try {
    (window.plausible as unknown as { init: (i?: unknown) => void }).init();
  } catch {
    // noop — script will initialise itself on load.
  }
}

/**
 * Stop firing events for the rest of the session and remove the
 * Plausible globals. The `<script>` tag stays in place (browsers don't
 * support meaningful unloading), but tearing down `window.plausible`
 * means subsequent `trackEvent` calls become no-ops.
 */
export function unloadPlausible(): void {
  if (typeof window === "undefined") return;
  plausibleLoaded = false;
  try {
    delete (window as { plausible?: unknown }).plausible;
  } catch {
    window.plausible = undefined;
  }
}

export function trackEvent(name: string, props?: PlausibleProps): void {
  if (typeof window === "undefined") return;
  try {
    if (typeof window.plausible === "function") {
      window.plausible(name, props ? { props } : undefined);
    }
  } catch {
    // Never let analytics break the page.
  }
}

/**
 * Fires a "scroll_depth" Plausible event at 25/50/75/100% of the
 * scrollable viewport height — once per threshold per mount. Cleanup
 * removes the scroll listener so this is safe to mount once on the
 * landing page.
 */
export function useScrollDepthTracking(eventName = "scroll_depth"): void {
  const fired = useRef<Set<number>>(new Set());

  useEffect(() => {
    const thresholds = [25, 50, 75, 100] as const;

    function onScroll(): void {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - doc.clientHeight;
      if (scrollable <= 0) return;
      const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      for (const t of thresholds) {
        if (pct >= t && !fired.current.has(t)) {
          fired.current.add(t);
          trackEvent(eventName, { depth: `${t}` });
        }
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [eventName]);
}
